import { Button } from '@alison/ui';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { type KeyboardEvent, type ReactNode, useRef, useState } from 'react';
import { FilterCheckbox } from './FilterCheckbox';
import { MenuSearchInput } from './MenuSearchInput';
import { filterOptionItemSx } from './menuStyles';

const OPTION_LIST_SX = { maxHeight: 280, overflowY: 'auto' } as const;

export interface FilterOption {
  id: string;
  label: string;
}

interface FilterOptionMenuProps {
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (ids: string[]) => void;
  onSearchChange?: (query: string) => void;
  isLoading?: boolean;
  truncated?: boolean;
}

export function FilterOptionMenu({
  label,
  options,
  selected,
  onChange,
  onSearchChange,
  isLoading = false,
  truncated = false,
}: FilterOptionMenuProps): ReactNode {
  const [query, setQuery] = useState('');

  // Options come from a truncated/search-scoped page, so a previously picked id can drop out of
  // it. Remember every label we've seen so a selected item stays visible (and named) once it
  // scrolls out of the current page or search results.
  const knownLabels = useRef(new Map<string, string>());
  for (const option of options) knownLabels.current.set(option.id, option.label);

  // Pin whatever was selected when the menu opened, so all of it is visible right away. Frozen
  // for the life of this open — ids picked afterward keep their natural spot instead of jumping.
  const [pinnedIds] = useState(() => new Set(selected));

  const matchesQuery = (option: FilterOption): boolean =>
    !query || option.label.toLowerCase().includes(query.toLowerCase());
  const pinnedOptions = Array.from(pinnedIds)
    .map((id) => ({ id, label: knownLabels.current.get(id) ?? id }))
    .filter(matchesQuery);
  const unpinnedMatches = options.filter(
    (option) => !pinnedIds.has(option.id) && matchesQuery(option),
  );
  const filteredOptions = [...pinnedOptions, ...unpinnedMatches];
  const filteredIds = filteredOptions.map((option) => option.id);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));
  const someFilteredSelected = filteredIds.some((id) => selected.includes(id));

  const toggleOption = (id: string): void => {
    onChange(selected.includes(id) ? selected.filter((value) => value !== id) : [...selected, id]);
  };

  const toggleSelectAll = (): void => {
    onChange(
      allFilteredSelected
        ? selected.filter((id) => !filteredIds.includes(id))
        : Array.from(new Set([...selected, ...filteredIds])),
    );
  };

  return (
    <Box sx={{ minWidth: 240, maxWidth: 360 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 1, pb: 1 }}>
        <Box
          role="checkbox"
          tabIndex={0}
          aria-checked={someFilteredSelected ? (allFilteredSelected ? 'true' : 'mixed') : 'false'}
          aria-label="Select all"
          title="Select all"
          onMouseDown={(event) => event.preventDefault()}
          onClick={toggleSelectAll}
          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggleSelectAll();
            }
          }}
          sx={{ display: 'inline-flex', cursor: filteredIds.length ? 'pointer' : 'default' }}
        >
          <FilterCheckbox
            checked={allFilteredSelected}
            indeterminate={!allFilteredSelected && someFilteredSelected}
          />
        </Box>
        <MenuSearchInput
          autoFocus
          placeholder={`Find ${label}…`}
          value={query}
          onChange={(value) => {
            setQuery(value);
            onSearchChange?.(value);
          }}
        />
      </Box>
      <Box data-testid="filter-option-list" sx={OPTION_LIST_SX}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 120,
              py: 4,
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : filteredOptions.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Box component="span" sx={{ fontSize: 12, color: 'text.disabled' }}>
              No matches
            </Box>
          </Box>
        ) : (
          filteredOptions.map((option) => (
            <MenuItem
              key={option.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => toggleOption(option.id)}
              sx={filterOptionItemSx}
            >
              <FilterCheckbox checked={selected.includes(option.id)} />
              <Tooltip
                title={option.label}
                placement="top"
                disableInteractive
                slotProps={{
                  popper: { modifiers: [{ name: 'offset', options: { offset: [0, -6] } }] },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.label}
                </Box>
              </Tooltip>
            </MenuItem>
          ))
        )}
      </Box>
      {truncated ? (
        <Box sx={{ px: 2, pt: 1 }}>
          <Box component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>
            Showing first {options.length} — type to search for more
          </Box>
        </Box>
      ) : null}
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          pt: 2,
          pr: 2,
          pb: 1,
          pl: 2,
          mt: 1,
          borderTop: '1px solid',
          borderColor: theme.palette.divider,
        })}
      >
        <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled' }}>
          {selected.length ? `${selected.length} selected` : 'None selected'}
        </Box>
        {selected.length ? (
          <Button size="small" onClick={() => onChange([])} sx={{ minWidth: 0, p: 0 }}>
            Clear
          </Button>
        ) : null}
      </Box>
    </Box>
  );
}
